/**
 * Theme Toggle Component for habitué
 * Provides a beautiful animated toggle between dark and light modes
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Monitor } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';

// Simple toggle button (for nav/header)
export const ThemeToggle = ({ className = '', showLabel = false }) => {
  const { theme, toggleTheme, isDark } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`relative flex items-center gap-2 p-2 rounded-full transition-colors ${
        isDark 
          ? 'bg-[#1E2F26] hover:bg-[#283D32] text-[#C3A365]' 
          : 'bg-white hover:bg-gray-100 text-[#8B7355] shadow-sm border border-gray-200'
      } ${className}`}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="theme-toggle"
    >
      <motion.div
        initial={false}
        animate={{ rotate: isDark ? 0 : 180 }}
        transition={{ duration: 0.3 }}
      >
        {isDark ? <Moon size={18} weight="fill" /> : <Sun size={18} weight="fill" />}
      </motion.div>
      {showLabel && (
        <span className="text-xs font-medium">
          {isDark ? 'Dark' : 'Light'}
        </span>
      )}
    </button>
  );
};

// Expanded toggle with system option (for settings page)
export const ThemeSelector = ({ language = 'en' }) => {
  const { theme, setTheme, isDark } = useTheme();
  
  const options = [
    { 
      id: 'light', 
      icon: Sun, 
      label: language === 'fr' ? 'Clair' : 'Light',
      description: language === 'fr' ? 'Thème clair' : 'Light theme'
    },
    { 
      id: 'dark', 
      icon: Moon, 
      label: language === 'fr' ? 'Sombre' : 'Dark',
      description: language === 'fr' ? 'Thème sombre' : 'Dark theme'
    },
    { 
      id: 'system', 
      icon: Monitor, 
      label: language === 'fr' ? 'Système' : 'System',
      description: language === 'fr' ? 'Suivre le système' : 'Follow system'
    },
  ];

  const isManual = localStorage.getItem('habitue-theme-manual');
  const currentSelection = isManual ? theme : 'system';

  return (
    <div className={`rounded-xl p-1 flex gap-1 ${
      isDark ? 'bg-[#1E2F26]' : 'bg-gray-100'
    }`}>
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = currentSelection === option.id;
        
        return (
          <button
            key={option.id}
            onClick={() => setTheme(option.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg transition-all ${
              isActive
                ? isDark
                  ? 'bg-[#C3A365] text-[#0F1A14]'
                  : 'bg-white text-[#0F1A14] shadow-sm'
                : isDark
                  ? 'text-[#8BA89A] hover:text-white'
                  : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`theme-${option.id}`}
          >
            <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
            <span className="text-sm font-medium">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// Animated sun/moon toggle (fancy version)
export const AnimatedThemeToggle = ({ size = 24 }) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`relative w-14 h-8 rounded-full transition-colors ${
        isDark ? 'bg-[#1E2F26]' : 'bg-blue-100'
      }`}
      aria-label="Toggle theme"
      data-testid="animated-theme-toggle"
    >
      {/* Track decoration */}
      <div className="absolute inset-0 overflow-hidden rounded-full">
        {/* Stars (visible in dark mode) */}
        <motion.div
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: isDark ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute top-1.5 left-2 w-1 h-1 bg-white rounded-full opacity-60" />
          <div className="absolute top-3 left-4 w-0.5 h-0.5 bg-white rounded-full opacity-40" />
          <div className="absolute bottom-2 left-3 w-0.5 h-0.5 bg-white rounded-full opacity-50" />
        </motion.div>
        
        {/* Clouds (visible in light mode) */}
        <motion.div
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: isDark ? 0 : 1 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute top-1 right-2 w-3 h-1.5 bg-white rounded-full opacity-80" />
          <div className="absolute bottom-1.5 right-4 w-2 h-1 bg-white rounded-full opacity-60" />
        </motion.div>
      </div>

      {/* Toggle knob with icon */}
      <motion.div
        className={`absolute top-1 w-6 h-6 rounded-full flex items-center justify-center ${
          isDark ? 'bg-[#283D32]' : 'bg-yellow-400'
        }`}
        initial={false}
        animate={{ 
          x: isDark ? 4 : 28,
        }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      >
        {isDark ? (
          <Moon size={14} weight="fill" className="text-[#C3A365]" />
        ) : (
          <Sun size={14} weight="fill" className="text-yellow-600" />
        )}
      </motion.div>
    </button>
  );
};

export default ThemeToggle;
